import { useMemo } from 'react';

import { isMemberLoggedIn, useMemberId } from '@trello/authentication';
import { useBoardMembers } from '@trello/business-logic-react/board';
import { allowsSelfJoin } from '@trello/business-logic/board';
import { isPremOrganizationAdmin } from '@trello/business-logic/organization';

import { useBoardJoinButtonBoardQuery } from './BoardJoinButtonBoardQuery.generated';
import { useBoardJoinButtonMemberQuery } from './BoardJoinButtonMemberQuery.generated';

interface UseBoardJoinButtonProps {
  boardId: string;
}

export const useBoardJoinButton = ({ boardId }: UseBoardJoinButtonProps) => {
  const memberId = useMemberId();
  const { isAdmin, isMember, isMemberOfOrganization } =
    useBoardMembers(boardId);

  const { data: boardData } = useBoardJoinButtonBoardQuery({
    skip: !isMemberLoggedIn(),
    variables: {
      boardId,
    },
    fetchPolicy: 'cache-only',
    waitOn: ['CurrentBoardInfo'],
  });

  const { data: memberData } = useBoardJoinButtonMemberQuery({
    skip: !isMemberLoggedIn(),
    variables: {
      memberId,
    },
    fetchPolicy: 'cache-only',
    waitOn: ['MemberHeader'],
  });

  const board = boardData?.board;
  const organization = board?.organization;
  const member = memberData?.member;

  const confirmed = member?.confirmed ?? false;

  const memberType = useMemo(
    () => (isAdmin(memberId) ? 'admin' : 'normal'),
    [isAdmin, memberId],
  );

  const showJoinButton = useMemo(() => {
    if (!board || !member) {
      return false;
    }

    if (!board.idOrganization && !board.idEnterprise) {
      return false;
    }

    if (isMember(memberId)) {
      return false;
    }

    const isPremOrgAdmin = isPremOrganizationAdmin(
      member,
      board.idOrganization ?? '',
    );

    if (isPremOrgAdmin) {
      return true;
    }

    const isOrgMember = isMemberOfOrganization(memberId);
    const canSelfJoin = allowsSelfJoin(board);

    if (isOrgMember && canSelfJoin) {
      return true;
    }

    const isEnterpriseBoard =
      !!board.idEnterprise || !!organization?.enterprise;

    if (
      isEnterpriseBoard &&
      organization?.enterprise?.idAdmins?.includes(member.id)
    ) {
      return true;
    }

    return false;
  }, [
    board,
    member,
    isMember,
    memberId,
    isMemberOfOrganization,
    organization?.enterprise,
  ]);

  return {
    confirmed,
    memberType,
    showJoinButton,
  };
};
